/**
 * Servicio centralizado de configuración para la aplicación
 * Maneja tanto valores hardcodeados para desarrollo como datos reales de producción
 */

export interface AppConfig {
  workpillId: string | null;
  executionId: string | null;
  /** workpill_execution_templates.id for the DOCX open in Word (Storage prefix + redline target) */
  activeExecutionTemplateId: string | null;
  userId: string | null;
  isDevelopment: boolean;
  deploymentMode: 'b2b' | 'b2c';
}

/**
 * Configuración por defecto
 * El userId ahora se establece después del login
 */
const DEFAULT_CONFIG: AppConfig = {
  workpillId: null,
  executionId: null,
  activeExecutionTemplateId: null,
  userId: null, // Se establecerá después del login
  isDevelopment: true,
  deploymentMode: (process.env.VITE_DEPLOYMENT_MODE as 'b2b' | 'b2c') || 'b2b'
};

class ConfigService {
  private config: AppConfig;

  constructor() {
    this.config = DEFAULT_CONFIG;
  }

  /**
   * Inicializa la configuración con datos reales
   */
  public initialize(config: Partial<AppConfig>): void {
    this.config = {
      ...this.config,
      ...config,
      isDevelopment: config.isDevelopment ?? this.config.isDevelopment
    };

    console.log('⚙️ Config initialized:', {
      hasUserId: !!this.config.userId,
      hasWorkpillId: !!this.config.workpillId,
      hasExecutionId: !!this.config.executionId,
      isDevelopment: this.config.isDevelopment
    });
  }

  /**
   * Establecer el usuario autenticado
   */
  public setUserId(userId: string): void {
    this.config.userId = userId;
    console.log('👤 User ID set:', userId);
  }

  /**
   * Limpiar configuración al hacer logout
   */
  public clearAuth(): void {
    this.config.userId = null;
    this.config.workpillId = null;
    this.config.executionId = null;
    this.config.activeExecutionTemplateId = null;
    console.log('🔐 Auth cleared');
  }

  public setActiveExecutionTemplateId(id: string | null): void {
    this.config.activeExecutionTemplateId = id;
    if (id) {
      console.log('📄 Active execution template set:', id);
    }
  }

  /**
   * Obtiene la configuración actual
   */
  public getConfig(): AppConfig {
    return { ...this.config };
  }

  /**
   * Getters individuales para facilitar el uso
   */
  public getWorkpillId(): string {
    if (!this.config.workpillId) {
      console.warn('⚠️ workpillId no está configurado. El usuario debe seleccionar una ejecución.');
      return ''; // Retorna string vacío en lugar de null para compatibilidad
    }
    return this.config.workpillId;
  }

  public getExecutionId(): string {
    if (!this.config.executionId) {
      console.warn('⚠️ executionId no está configurado. El usuario debe seleccionar una ejecución.');
      return ''; // Retorna string vacío en lugar de null para compatibilidad
    }
    return this.config.executionId;
  }

  public getUserId(): string | null {
    return this.config.userId;
  }

  public isDevelopment(): boolean {
    return this.config.isDevelopment;
  }

  public getDeploymentMode(): 'b2b' | 'b2c' {
    return this.config.deploymentMode;
  }

  public isB2C(): boolean {
    return this.config.deploymentMode === 'b2c';
  }

  /**
   * Verificar si el usuario está autenticado
   */
  public isAuthenticated(): boolean {
    return this.config.userId !== null;
  }

  /**
   * Para debugging - muestra la configuración actual
   */
  public logCurrentConfig(): void {
    console.log('📋 Current Config:', this.config);
  }
}

// Singleton instance
export const configService = new ConfigService();



